import { z } from 'zod';

/**
 * Attendance (الحضور والانصراف) — schedules, holidays, check-in/out sessions and
 * the per-day judgement built from them. See docs/PRD-attendance.md.
 *
 * Every wall-clock value that crosses the wire is SHOP-LOCAL, in the tenant's
 * attendance time zone: `HH:MM` for a schedule, `YYYY-MM-DDTHH:MM` for a session.
 * The API does the zone arithmetic once; no browser has to know the offset, and
 * an owner reading the board from abroad still sees the shop's own clock.
 */

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM');
/** A shop-local wall-clock instant. */
export const LocalDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/, 'expected YYYY-MM-DDTHH:MM');

// -----------------------------------------------------------------------------
// Work schedules
// -----------------------------------------------------------------------------

/**
 * One stretch of work. An `end` at or before its `start` runs past midnight —
 * 18:00 → 02:00 is an evening shift that finishes the next morning.
 */
export const WorkPeriodSchema = z
  .object({ start: clockTime, end: clockTime })
  .refine((p) => p.start !== p.end, { message: 'a period cannot start and end at the same time', path: ['end'] });
export type WorkPeriod = z.infer<typeof WorkPeriodSchema>;

const toMin = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** True when no two periods of one day overlap (an end at or before its start runs past midnight). */
export function periodsDisjoint(day: ReadonlyArray<{ start: string; end: string }>): boolean {
  const spans = day
    .map((p) => {
      const start = toMin(p.start);
      let end = toMin(p.end);
      if (end <= start) end += 1440;
      return { start, end };
    })
    .sort((a, b) => a.start - b.start);
  return spans.every((s, i) => i === 0 || spans[i - 1]!.end <= s.start);
}

/**
 * Seven days, indexed by weekday: 0 = Sunday … 6 = Saturday. An empty day is a
 * day off. Two periods of one day may not overlap — 09:00–17:00 plus 16:00–21:00
 * would count the same hour twice.
 */
export const WorkWeekSchema = z
  .array(z.array(WorkPeriodSchema).max(4).refine(periodsDisjoint, { message: 'two periods of one day overlap' }))
  .length(7);
export type WorkWeek = z.infer<typeof WorkWeekSchema>;

/**
 * The same shape without the overlap rule, for READING what is stored: a week
 * saved before the rule existed must still judge its days, not collapse to
 * seven days off.
 */
export const WorkWeekStoredSchema = z.array(z.array(WorkPeriodSchema).max(4)).length(7);

export const WorkScheduleCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  periods: WorkWeekSchema,
  /** Minutes of lateness (and early leave) tolerated before it counts at all. */
  graceMinutes: z.number().int().min(0).max(240).optional(),
  /** Extra time shorter than this is not overtime — a few minutes after closing is not a claim. */
  minOvertimeMinutes: z.number().int().min(0).max(720).optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type WorkScheduleCreate = z.infer<typeof WorkScheduleCreateSchema>;

export const WorkScheduleUpdateSchema = WorkScheduleCreateSchema.partial();
export type WorkScheduleUpdate = z.infer<typeof WorkScheduleUpdateSchema>;

export interface WorkSchedule {
  id: string;
  name: string;
  periods: WorkWeek;
  graceMinutes: number;
  minOvertimeMinutes: number;
  isDefault: boolean;
  isActive: boolean;
  /** Employees assigned to it directly (not counting those who fall back to the default). */
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

// -----------------------------------------------------------------------------
// Holidays
// -----------------------------------------------------------------------------

export const HolidayCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    startDate: dateString,
    endDate: dateString,
  })
  .refine((h) => h.endDate >= h.startDate, { message: 'the holiday ends before it starts', path: ['endDate'] });
export type HolidayCreate = z.infer<typeof HolidayCreateSchema>;

export const HolidayUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  startDate: dateString.optional(),
  endDate: dateString.optional(),
});
export type HolidayUpdate = z.infer<typeof HolidayUpdateSchema>;

export interface Holiday {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
}

// -----------------------------------------------------------------------------
// Settings
// -----------------------------------------------------------------------------

export const AttendancePayrollPolicySchema = z.object({
  /** Master switch. Off, a payroll run is exactly what it was before attendance existed. */
  enabled: z.boolean().default(false),
  deductAbsence: z.boolean().default(true),
  deductLate: z.boolean().default(true),
  payOvertime: z.boolean().default(false),
  overtimeMultiplier: z.string().regex(/^\d{1,2}(\.\d{1,2})?$/).default('1.5'),
  /** Day rate = monthly ÷ 30 (Iraqi labour practice) or ÷ the scheduled working days in the period. */
  dayRateDivisor: z.enum(['thirty', 'scheduled']).default('thirty'),
});
export type AttendancePayrollPolicy = z.infer<typeof AttendancePayrollPolicySchema>;

export const AttendanceSettingsSchema = z.object({
  /** IANA zone the shop's clock runs in. Production servers run UTC; the shop does not. */
  timezone: z.string().min(1).max(64).default('Asia/Baghdad'),
  /** Staff with a login may clock themselves in from their own session. */
  selfServiceEnabled: z.boolean().default(true),
  /** The kiosk asks for a PIN even from employees who have not been given one (they cannot use it). */
  kioskRequirePin: z.boolean().default(true),
  /**
   * The first day attendance is judged. A shop that switches this on in the
   * middle of the month must not find everybody "absent" for the days before it
   * started punching: before this date a day with no session is `untracked`.
   * Set automatically when the first work schedule is created; null judges every day.
   */
  trackFrom: dateString.nullable().default(null),
  payroll: AttendancePayrollPolicySchema.default({}),
});
export type AttendanceSettings = z.infer<typeof AttendanceSettingsSchema>;

export const AttendanceSettingsUpdateSchema = AttendanceSettingsSchema.partial().extend({
  payroll: AttendancePayrollPolicySchema.partial().optional(),
});
export type AttendanceSettingsUpdate = z.infer<typeof AttendanceSettingsUpdateSchema>;

/**
 * Stored settings → complete settings. Anything missing or malformed falls back
 * to its default rather than failing, so a half-written row can never take the
 * attendance screens down.
 */
export function parseAttendanceSettings(raw: unknown): AttendanceSettings {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try { obj = JSON.parse(raw); } catch { obj = {}; }
  }
  const parsed = AttendanceSettingsSchema.safeParse(obj ?? {});
  if (parsed.success) return parsed.data;
  // Salvage field by field: one bad value must not reset the rest.
  const src = (obj && typeof obj === 'object' ? obj : {}) as Record<string, unknown>;
  const pick = <S extends z.ZodTypeAny>(schema: S, v: unknown, fallback: z.output<S>): z.output<S> => {
    const r = schema.safeParse(v);
    return r.success ? (r.data as z.output<S>) : fallback;
  };
  const base = AttendanceSettingsSchema.parse({});
  const payrollSrc = (src.payroll && typeof src.payroll === 'object' ? src.payroll : {}) as Record<string, unknown>;
  const payrollShape = AttendancePayrollPolicySchema.shape;
  return {
    timezone: pick(z.string().min(1).max(64), src.timezone, base.timezone),
    selfServiceEnabled: pick(z.boolean(), src.selfServiceEnabled, base.selfServiceEnabled),
    kioskRequirePin: pick(z.boolean(), src.kioskRequirePin, base.kioskRequirePin),
    trackFrom: pick(dateString.nullable(), src.trackFrom, base.trackFrom),
    payroll: {
      enabled: pick(payrollShape.enabled, payrollSrc.enabled, base.payroll.enabled),
      deductAbsence: pick(payrollShape.deductAbsence, payrollSrc.deductAbsence, base.payroll.deductAbsence),
      deductLate: pick(payrollShape.deductLate, payrollSrc.deductLate, base.payroll.deductLate),
      payOvertime: pick(payrollShape.payOvertime, payrollSrc.payOvertime, base.payroll.payOvertime),
      overtimeMultiplier: pick(payrollShape.overtimeMultiplier, payrollSrc.overtimeMultiplier, base.payroll.overtimeMultiplier),
      dayRateDivisor: pick(payrollShape.dayRateDivisor, payrollSrc.dayRateDivisor, base.payroll.dayRateDivisor),
    },
  };
}

// -----------------------------------------------------------------------------
// Sessions (one check-in → check-out)
// -----------------------------------------------------------------------------

export const AttendanceSourceSchema = z.enum(['web', 'kiosk', 'manual', 'import']);
export type AttendanceSource = z.infer<typeof AttendanceSourceSchema>;

export const AttendanceRecordCreateSchema = z.object({
  employeeId: z.string().uuid(),
  checkIn: LocalDateTimeSchema,
  checkOut: LocalDateTimeSchema.nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export type AttendanceRecordCreate = z.infer<typeof AttendanceRecordCreateSchema>;

export const AttendanceRecordUpdateSchema = z.object({
  checkIn: LocalDateTimeSchema.optional(),
  /** `null` re-opens the session (the person is still in). */
  checkOut: LocalDateTimeSchema.nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export type AttendanceRecordUpdate = z.infer<typeof AttendanceRecordUpdateSchema>;

export interface AttendanceRecord {
  id: string;
  employeeId: string;
  workDate: string;
  /** UTC instants. */
  checkInAt: string;
  checkOutAt: string | null;
  /** The same instants on the shop's clock, `YYYY-MM-DDTHH:MM`. */
  checkIn: string;
  checkOut: string | null;
  checkInSource: AttendanceSource;
  checkOutSource: AttendanceSource | null;
  note: string | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export const AttendancePunchActionSchema = z.enum(['in', 'out']);
export type AttendancePunchAction = z.infer<typeof AttendancePunchActionSchema>;

/** A manager clocking somebody in or out right now. Omitted action = toggle. */
export const AttendanceManagerPunchSchema = z.object({
  employeeId: z.string().uuid(),
  action: AttendancePunchActionSchema.optional(),
});
export type AttendanceManagerPunch = z.infer<typeof AttendanceManagerPunchSchema>;

/** The kiosk: an employee code (typed or scanned from a badge) plus PIN. Always a toggle. */
export const AttendanceKioskPunchSchema = z.object({
  code: z.string().trim().min(1).max(64),
  pin: z.string().regex(/^\d{4,6}$/).optional(),
});
export type AttendanceKioskPunch = z.infer<typeof AttendanceKioskPunchSchema>;

export const AttendancePinSchema = z.object({
  pin: z.string().regex(/^\d{4,6}$/, 'a PIN is 4 to 6 digits').nullable(),
});
export type AttendancePin = z.infer<typeof AttendancePinSchema>;

// -----------------------------------------------------------------------------
// The judgement of a day
// -----------------------------------------------------------------------------

export const AttendanceDayStatusSchema = z.enum([
  'present',      // at least one session on a working day
  'absent',       // a working day, no session, and the first period (+ grace) has passed
  'not_yet',      // today, before the first period (+ grace)
  'untracked',    // before attendance tracking began, and nothing recorded
  'leave',        // an approved leave request covers the day
  'holiday',      // a public holiday
  'off',          // the schedule has no work that weekday
  'upcoming',     // a future working day
  'unscheduled',  // no schedule to judge against, and nothing recorded
  'not_employed', // before the hire date or after termination
]);
export type AttendanceDayStatus = z.infer<typeof AttendanceDayStatusSchema>;

export interface AttendanceDayMetrics {
  status: AttendanceDayStatus;
  /** Minutes the schedule expected on a working day; 0 on days nobody is expected. */
  scheduledMinutes: number;
  workedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  /** Whole periods with no session in them — the evening half of a split shift nobody turned up for. */
  missedMinutes: number;
  overtimeMinutes: number;
  /** Clocked in and not out yet (today). */
  open: boolean;
  /** Never clocked out on a past day — needs a manager. */
  missingCheckout: boolean;
  /** Only on `leave` days. */
  leavePaid: boolean | null;
  /** Only on `holiday` days. */
  holidayName: string | null;
}

export interface AttendanceEmployeeRef {
  id: string;
  code: string;
  displayName: string;
  warehouseId: string | null;
  scheduleId: string | null;
  scheduleName: string | null;
}

export interface AttendanceBoardRow {
  employee: AttendanceEmployeeRef;
  day: AttendanceDayMetrics;
  /** Shop-local `HH:MM`, or null. */
  firstIn: string | null;
  lastOut: string | null;
  sessions: AttendanceRecord[];
}

export interface AttendanceBoardSummary {
  tracked: number;
  present: number;
  late: number;
  working: number;
  absent: number;
  notYet: number;
  leave: number;
  off: number;
  holiday: number;
  missingCheckout: number;
}

export interface AttendanceBoard {
  date: string;
  today: string;
  /** The shop's clock right now, `YYYY-MM-DDTHH:MM`. */
  now: string;
  timezone: string;
  rows: AttendanceBoardRow[];
  summary: AttendanceBoardSummary;
}

export interface AttendanceTotals {
  /** Scheduled working days in range (holidays excluded, leave included) — the payroll day count. */
  workingDays: number;
  /** Days somebody was expected and it is already judged: present + absent. */
  expectedDays: number;
  presentDays: number;
  absentDays: number;
  leaveDays: number;
  unpaidLeaveDays: number;
  holidayDays: number;
  offDays: number;
  lateDays: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  missedMinutes: number;
  workedMinutes: number;
  overtimeMinutes: number;
  /** Planned minutes across `workingDays` — divides into the minute rate. */
  plannedMinutes: number;
  missingCheckouts: number;
}

export interface AttendanceReportDay {
  date: string;
  status: AttendanceDayStatus;
  workedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  overtimeMinutes: number;
  missingCheckout: boolean;
}

export interface AttendanceReportRow {
  employee: AttendanceEmployeeRef;
  totals: AttendanceTotals;
  days: AttendanceReportDay[];
}

export interface AttendanceReport {
  from: string;
  to: string;
  today: string;
  timezone: string;
  rows: AttendanceReportRow[];
}

/** What the clock card needs about the signed-in person. */
export interface AttendanceMe {
  employee: { id: string; code: string; displayName: string; trackAttendance: boolean };
  selfServiceEnabled: boolean;
  timezone: string;
  now: string;
  today: AttendanceBoardRow;
  /** The session a clock-out would close, if any. */
  openSession: AttendanceRecord | null;
  month: { from: string; to: string; totals: AttendanceTotals };
}

/** One session the import found, and what happens to it. */
export interface AttendanceImportSession {
  employeeId: string;
  code: string;
  displayName: string;
  workDate: string;
  /** Shop-local `YYYY-MM-DDTHH:MM`. */
  checkIn: string;
  checkOut: string | null;
  /**
   * new       — will be (or was) added
   * existing  — already on file with the same check-in: skipped, so a re-import adds nothing
   * closes    — on file but open; the import supplies the check-out it was missing
   * conflict  — overlaps a different session of the same person: skipped for a manager
   */
  status: 'new' | 'existing' | 'closes' | 'conflict';
}

export interface AttendanceImportResult {
  dryRun: boolean;
  timezone: string;
  /** Punch rows read from the file. */
  punches: number;
  /** Data rows (1-based) with an id but no readable date/time — the first 50. */
  badRows: number[];
  /** Ids no employee answers to (device number or code). */
  unknownIds: Array<{ id: string; name: string | null; punches: number }>;
  /** Punches of employees who are not tracked or no longer active. */
  skippedUntracked: number;
  employees: number;
  /** The same finger twice within two minutes. */
  doubleScans: number;
  unpaired: number;
  counts: { new: number; existing: number; closes: number; conflict: number };
  /** The first 300 sessions, for the preview table. */
  sessions: AttendanceImportSession[];
}

export interface AttendancePunchResult {
  action: AttendancePunchAction;
  record: AttendanceRecord;
  employee: { id: string; code: string; displayName: string };
  /** The day as it stands after the punch. */
  day: AttendanceDayMetrics;
}
